/**
 * FrameworkProvider — 프레임워크 전역 Context 프로바이더.
 *
 * 【역할】
 * - antd App.useApp() 의 message/notification/modal 인스턴스를 앱 전역에 노출.
 * - 전역 로딩 카운터(pushLoading/popLoading) 관리 + 부팅 시 fw_label 번들 자동 로드.
 * - axios 인터셉터가 발행하는 http-warn/http-error 이벤트를 antd message 로 표시.
 *
 * 【주요 기능】
 * - FrameworkProvider: <App> 루트 children 래퍼
 * - useFrameworkContext(): Context 소비 훅 (프로바이더 외부 호출 시 throw)
 * - message/notification/modal 노출 (useMessage 훅이 내부적으로 사용)
 * - loading 상태 + setLoading/pushLoading/popLoading
 *
 * 【부트스트랩 순서】
 * 1) token 존재 시 GET /labels?locale=<current> → addResourceBundle
 * 2) LOCALE_CHANGE_EVENT 구독 → 언어 전환 시 재로드
 * 3) fw:http-warn/fw:http-error 이벤트 → antd message.warning/error 토스트
 *
 * 【사용 예시】
 * ```tsx
 * // App.tsx
 * <AntApp>
 *   <FrameworkProvider>
 *     <AppRoutes />
 *   </FrameworkProvider>
 * </AntApp>
 * ```
 *
 * 【왜 이 패턴?】
 * - antd message/notification 은 App.useApp() Context 하위에서만 동작. 전역 singleton 불가.
 * - useLocale.setLocale 은 언어 전환 시 번들을 재로드하지만, 최초 로그인 직후에는
 *   setLocale 이 호출되지 않으므로 Provider 가 token 감지 후 한 번 끌어온다.
 */
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  ReactNode
} from 'react'
import { App as AntApp } from 'antd'
import { useTranslation } from 'react-i18next'
import http from './api/axios'
import { useAuthStore } from '@/store/authStore'
import { LOCALE_CHANGE_EVENT } from './useLocale'

type AntAppHooks = ReturnType<typeof AntApp.useApp>

/**
 * FrameworkContextValue — useFrameworkContext 의 반환 타입.
 * loading 은 pushLoading 카운터 > 0 인지의 불리언, setLoading 은 강제 on/off.
 */
export interface FrameworkContextValue {
  message: AntAppHooks['message']
  notification: AntAppHooks['notification']
  modal: AntAppHooks['modal']
  loading: boolean
  setLoading: (v: boolean) => void
  pushLoading: () => void
  popLoading: () => void
}

const FrameworkContext = createContext<FrameworkContextValue | null>(null)

/**
 * FrameworkProvider — 프레임워크 Context 프로바이더 컴포넌트.
 * @param children - 프로바이더 내부에서 useFrameworkContext 를 호출할 하위 트리.
 */
export function FrameworkProvider({ children }: { children: ReactNode }) {
  const { message, notification, modal } = AntApp.useApp()
  const [loadCount, setLoadCount] = useState(0)
  const { i18n } = useTranslation()
  const token = useAuthStore((s) => s.token)

  const pushLoading = useCallback(() => setLoadCount((c) => c + 1), [])
  const popLoading = useCallback(() => setLoadCount((c) => Math.max(0, c - 1)), [])
  const setLoading = useCallback((v: boolean) => setLoadCount(v ? 1 : 0), [])

  /**
   * 토큰이 있는 동안(로그인 후) 현재 locale 라벨 번들을 백엔드에서 로드한다.
   * - 초기 진입: 저장된 토큰으로 바로 로드
   * - 로그인 직후: token 상태 변경으로 effect 재실행
   * - LOCALE_CHANGE_EVENT: useLocale.setLocale 이 이미 bundle 을 갱신하지만,
   *   혹시 경로를 통해 호출되지 않은 경우를 대비해 동일 effect 가 재구동된다.
   */
  useEffect(() => {
    if (!token) return
    let cancelled = false
    const load = async (loc: string) => {
      try {
        const res = await http.get('/labels', { params: { locale: loc } })
        const bundle = res.data?.data ?? res.data ?? {}
        if (cancelled) return
        if (bundle && typeof bundle === 'object') {
          i18n.addResourceBundle(loc, 'translation', bundle, true, true)
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- i18next emit is internal API
            ;(i18n as any).emit?.('languageChanged', loc)
          } catch {
            /* noop */
          }
          // 페이지 reload 직후 i18n 번들이 비어있는 상태에서 seedMenuStore 가 ko fallback 으로
          // 메뉴를 시드하는 경합 회피 — labels 로드 완료 후 menu reseed 트리거.
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('fw:labels-loaded', { detail: loc }))
          }
        }
      } catch {
        /* noop — 라벨 로드 실패해도 fallback 문자열 사용 */
      }
    }
    load(i18n.language)

    const onLocaleChange = (e: Event) => {
      const loc = (e as CustomEvent).detail ?? i18n.language
      load(String(loc))
    }
    window.addEventListener(LOCALE_CHANGE_EVENT, onLocaleChange)
    return () => {
      cancelled = true
      window.removeEventListener(LOCALE_CHANGE_EVENT, onLocaleChange)
    }
  }, [token, i18n])

  /**
   * axios 인터셉터에서 발행하는 HTTP 경고/에러 이벤트를 antd message 로 표시.
   * - 'fw:http-warn'  → 403 권한 거부, 5xx 재시도 실패(임계 미달) 시 토스트 경고
   * - 'fw:http-error' → 5xx 임계 도달 → 서비스 불능 알림 (직후 로그인 리다이렉트)
   */
  useEffect(() => {
    const onWarn = (e: Event) => {
      const detail = (e as CustomEvent).detail as { message: string }
      void message.warning(detail.message, 3)
    }
    const onError = (e: Event) => {
      const detail = (e as CustomEvent).detail as { message: string }
      void message.error(detail.message, 5)
    }
    window.addEventListener('fw:http-warn', onWarn)
    window.addEventListener('fw:http-error', onError)
    return () => {
      window.removeEventListener('fw:http-warn', onWarn)
      window.removeEventListener('fw:http-error', onError)
    }
  }, [message])

  const value = useMemo<FrameworkContextValue>(
    () => ({
      message,
      notification,
      modal,
      loading: loadCount > 0,
      setLoading,
      pushLoading,
      popLoading
    }),
    [message, notification, modal, loadCount, setLoading, pushLoading, popLoading]
  )

  return <FrameworkContext.Provider value={value}>{children}</FrameworkContext.Provider>
}

/**
 * useFrameworkContext — Context 소비 훅.
 * FrameworkProvider 외부에서 호출 시 Error throw (설정 누락 조기 발견용).
 */
export function useFrameworkContext(): FrameworkContextValue {
  const ctx = useContext(FrameworkContext)
  if (!ctx) throw new Error('useFrameworkContext must be used inside <FrameworkProvider>')
  return ctx
}
